// Delay and Act now (qa-runs#1171 part 3; the table half is vtt/shared/src/state/delay.ts): on their turn a combatant
// presses Delay (the system's label for it) and is listed as waiting; Act now brings them in before whoever is due.
// Shown only where the system has a delay rule (`features.delay`, read through the table's extras). The waiting list is
// what this person was sent: the DM's whole; a player's only the waiting combatants they can see — a hidden delayer is
// never in their store, so it is never drawn (hidden means absent). Who may press: the DM, or the combatant's player;
// the server decides, and a refusal is said over the table. Used by the turn tracker and the initiative strip.
import {
  currentTurn, dmEncounterView, emptyEncounter, type Encounter, type PlayerEncounter,
} from '@dragons-table/vtt-shared';
import { useTableState } from '../../table/TableState';
import { useShell } from '../context';
import { useTableExtras } from '../../tableExtras';
import { useState } from 'react';
import { BTN } from '../ui';

// Touch-friendly (the issue's tablet check): a finger-sized target on a coarse pointer.
const btn = `${BTN} min-h-[2rem] [@media(pointer:coarse)]:min-h-[2.75rem] [@media(pointer:coarse)]:px-3`;

interface Waiting { id: string; round: number; pass?: number }
type WithWaiting<E> = E & { waiting?: Waiting[] };

/** Delay and Ready side by side: each shows only where the system has it. */
export function DelayControls({ compact = false }: { compact?: boolean }) {
  return <><WaitControls compact={compact} /><ReadyControls compact={compact} /></>;
}

interface Held { id: string; trigger: string; action: string }

/**
 * Ready (qa-runs#366; the table half is vtt/shared/src/state/ready.ts): on their turn a combatant states a trigger and a
 * kind of action; Fire brings them in before whoever acts now. What is held is what this person was sent: the DM's whole,
 * a player's only their own, so another's trigger is never in their store. Shown only where the system has `ready`.
 */
function ReadyControls({ compact }: { compact: boolean }) {
  const { store, role, act, setNotice, systemRules } = useTableState();
  const [open, setOpen] = useState(false);
  const [trigger, setTrigger] = useState('');
  const [action, setAction] = useState('');
  const rule = systemRules.ready;
  const isDm = role === 'dm';
  const raw = (store.state.encounter ?? emptyEncounter()) as (Encounter | PlayerEncounter) & { held?: Held[] };
  if (!rule || !raw.running) return null;
  const reading: PlayerEncounter = isDm ? dmEncounterView(raw as Encounter) : (raw as PlayerEncounter);
  const turn = currentTurn(raw);
  const active = turn ? reading.combatants.find((c) => c.id === turn.active) ?? null : null;
  const held = (raw.held ?? []).map((h) => ({ h, c: reading.combatants.find((c) => c.id === h.id) })).filter((x) => x.c);
  const label = (key: string) => systemRules.actions?.kinds.find((k) => k.key === key)?.label ?? key.replace(/_/g, ' ');
  const canReady = Boolean(turn && active && (isDm || active.mine) && !held.some((x) => x.h.id === active.id));
  const said = (fallback: string) => (r: { ok: boolean; reason?: string }) => { if (!r.ok) setNotice(r.reason ?? fallback); };
  if (!canReady && held.length === 0) return null;
  const kind = rule.kinds.includes(action) ? action : rule.kinds[0]!;
  return (
    <div className={compact ? 'flex flex-wrap items-center gap-1.5' : 'mt-1 flex flex-col gap-1 rounded border border-stone-700 p-1.5'} data-testid="ready-controls">
      {canReady && !open && <button type="button" className={btn} data-testid="ready-button" title="State a trigger and hold an action until it happens, or your next turn." onClick={() => setOpen(true)}>{rule.label}</button>}
      {canReady && open && (
        <form className="flex flex-wrap items-center gap-1.5" data-testid="ready-form" onSubmit={(e) => {
          e.preventDefault();
          if (!trigger.trim()) return;
          act({ kind: 'encounter.ready', id: active!.id, turn: turn!, trigger: trigger.trim(), action: kind } as never, said('That action could not be readied.'));
          setOpen(false); setTrigger('');
        }}>
          <input className="min-w-0 flex-1 rounded border border-stone-600 bg-stone-900 px-1.5 py-0.5 text-xs" aria-label="When" placeholder="When…" maxLength={200} data-testid="ready-trigger" value={trigger} onChange={(e) => setTrigger(e.target.value)} />
          <select className="rounded border border-stone-600 bg-stone-900 px-1 py-0.5 text-xs" aria-label="Kind of action" data-testid="ready-kind" value={kind} onChange={(e) => setAction(e.target.value)}>
            {rule.kinds.map((k) => <option key={k} value={k}>{label(k)}</option>)}
          </select>
          <button type="submit" className={btn} data-testid="ready-hold">Hold</button>
          <button type="button" className={btn} onClick={() => setOpen(false)}>Cancel</button>
        </form>
      )}
      {held.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5" role="list" aria-label="Readied" data-testid="ready-held">
          {held.map(({ h, c }) => (
            <span key={h.id} role="listitem" className="flex items-center gap-1 rounded bg-stone-800 px-1.5 py-0.5 text-xs" data-testid={`held-${h.id}`}>
              <span className="italic">{c?.name ?? 'A combatant'}</span><span className="text-stone-400">{label(h.action)}: {h.trigger}</span>
              {h.id !== active?.id && <button type="button" className={btn} data-testid="ready-fire" title="The trigger happened: act now, before whoever is acting." onClick={() => act({ kind: 'encounter.fire', id: h.id } as never, said('They could not act now.'))}>Fire</button>}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

/** The waiting list and the Delay button. `compact` for the strip: one line. */
function WaitControls({ compact = false }: { compact?: boolean }) {
  const { store, role, act, setNotice } = useTableState();
  const { campaignId } = useShell();
  const { delay } = useTableExtras(campaignId);
  const isDm = role === 'dm';
  const raw = (store.state.encounter ?? emptyEncounter()) as WithWaiting<Encounter | PlayerEncounter>;
  if (!delay || !raw.running) return null;
  const reading: PlayerEncounter = isDm ? dmEncounterView(raw as Encounter) : (raw as PlayerEncounter);
  const turn = currentTurn(raw);
  const active = turn ? reading.combatants.find((c) => c.id === turn.active) ?? null : null;
  const waiting = (raw.waiting ?? []).map((w) => ({ w, c: reading.combatants.find((c) => c.id === w.id) })).filter((x) => x.c);
  const mayMove = (c: { mine: boolean }) => isDm || c.mine;
  const said = (fallback: string) => (r: { ok: boolean; reason?: string }) => { if (!r.ok) setNotice(r.reason ?? fallback); };
  const canDelay = Boolean(turn && active && mayMove(active) && !waiting.some((x) => x.w.id === active.id));
  const name = (c: { name?: string } | undefined) => c?.name ?? 'A combatant';
  const ends = delay.until === 'pass' ? (raw.passes ? 'at the end of the pass' : 'at the end of the round') : 'when their turn comes round again';
  if (!canDelay && waiting.length === 0) return null;
  return (
    <div className={compact ? 'flex flex-wrap items-center gap-1.5' : 'mt-1 flex flex-col gap-1 rounded border border-stone-700 p-1.5'} data-testid="delay-controls">
      {canDelay && (
        <button type="button" className={btn} data-testid="delay-button" title={`Step out of the order and act later; the wait ends ${ends}.`}
          onClick={() => act({ kind: 'encounter.delay', id: active!.id, turn: turn! } as never, said('That turn could not be delayed.'))}>
          {delay.label}
        </button>
      )}
      {waiting.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5" role="list" aria-label="Waiting" data-testid="delay-waiting">
          {!compact && <span className="text-xs text-stone-400" title={`A wait ends ${ends}.`}>Waiting:</span>}
          {waiting.map(({ w, c }) => (
            <span key={w.id} role="listitem" className="flex items-center gap-1 rounded bg-stone-800 px-1.5 py-0.5 text-xs" data-testid={`waiting-${w.id}`}>
              <span className="italic">{name(c)}</span><span className="text-stone-400">waiting</span>
              {mayMove(c!) && (
                <button type="button" className={btn} data-testid="act-now" title={delay.takesPlace ? 'Act before whoever is due, and keep that place in the order.' : 'Act before whoever is due; your place in the order stays.'}
                  onClick={() => act({ kind: 'encounter.act', id: w.id } as never, said('They could not act now.'))}>Act now</button>
              )}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
